import React from 'react';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Checkbox,
  Radio,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { isEmpty, find, findIndex, concat, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { t, canURL } from '~/helpers';

import * as S from './styles';

export default function Tabela({
  title,
  cells,
  data,
  height,
  size,
  clickOnCell,
  checkbox,
  radio,
  handleSubmit,
  unique,
  loading,
  defaultChecked,
}) {
  const [active, setActive] = React.useState(null);
  const [checked, setChecked] = React.useState([]);

  const permission = isEqual(canURL(), { read: true });

  // Indeterminate checkbox
  const indeterminate = React.useMemo(
    () => !isEmpty(checked) && checked.length < data.length,
    [checked, data]
  );

  // Checked checkbox
  const checkeds = React.useMemo(
    () => !isEmpty(data) && checked.length === data.length,
    [checked, data]
  );

  // Toggle all items
  const handleToggleAllItems = React.useCallback(() => {
    if (isEmpty(checked) || checked.length < data.length)
      setChecked(data.map((item) => item[unique]));
    else setChecked([]);
  }, [checked, data, unique]);

  // Toggle item
  const handleToggleItem = React.useCallback(
    (value) => {
      const index = findIndex(checked, (item) => item === value);
      const items = concat([], checked);

      if (index >= 0) items.splice(index, 1);
      else items.push(value);

      if (radio) {
        setChecked([value]);
      } else {
        setChecked(items);
      }
    },
    [checked, radio]
  );

  // Click cell
  const handleOnClickCell = React.useCallback((index, item) => {
    clickOnCell(item);
    setActive(index);
  }, []);

  React.useEffect(() => {
    setActive(null);
    setChecked([]);
  }, [data]);

  React.useEffect(() => {
    setChecked(defaultChecked);
  }, [defaultChecked]);

  return (
    <S.WrapperTabela height={height}>
      {loading && (
        <S.WrapperLoading>
          <CircularProgress />
        </S.WrapperLoading>
      )}

      <S.WrapperTitle>
        <Typography>{title}</Typography>

        {(checkbox || radio) && (
          <Button
            disabled={permission || isEmpty(checked)}
            onClick={() => handleSubmit(checked)}
            color="primary"
            variant="outlined"
            size="small"
          >
            {t('s.geral.botaoconfirmar')}
          </Button>
        )}
      </S.WrapperTitle>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table" size={size}>
          <TableHead>
            <TableRow>
              {checkbox && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={checkeds}
                    onChange={handleToggleAllItems}
                    disabled={permission || isEmpty(data)}
                    inputProps={{ 'aria-label': 'select all desserts' }}
                  />
                </TableCell>
              )}
              {radio && <TableCell padding="checkbox" />}
              {cells.map((cell, index) => (
                <TableCell
                  key={`cell-index-head-${index}`}
                  align={cell.align || 'left'}
                >
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow
                hover
                tabIndex={-1}
                key={`table-row-body-${index}`}
                onClick={() =>
                  checkbox || radio
                    ? handleToggleItem(item[unique])
                    : handleOnClickCell(index, item)
                }
                selected={
                  index === active ||
                  find(checked, (check) => check === item[unique])
                }
              >
                {checkbox && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      disabled={permission}
                      checked={Boolean(
                        find(checked, (check) => check === item[unique])
                      )}
                    />
                  </TableCell>
                )}

                {radio && (
                  <TableCell padding="checkbox">
                    <Radio
                      disabled={permission}
                      checked={Boolean(
                        find(checked, (check) => check === item[unique])
                      )}
                    />
                  </TableCell>
                )}

                {cells.map((cell, indexKey) => (
                  <TableCell
                    key={`table-cell-body-${indexKey}`}
                    align={cell.align || 'left'}
                  >
                    {cell.converter
                      ? cell.converter(item[cell.id])
                      : item[cell.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </S.WrapperTabela>
  );
}

Tabela.propTypes = {
  checkbox: PropTypes.bool,
  radio: PropTypes.bool,
  title: PropTypes.string.isRequired,
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      align: PropTypes.string,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  height: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  clickOnCell: PropTypes.func,
  handleSubmit: PropTypes.func,
  unique: PropTypes.string,
  loading: PropTypes.bool,
  defaultChecked: PropTypes.arrayOf(PropTypes.any),
};

Tabela.defaultProps = {
  checkbox: false,
  radio: false,
  height: '100%',
  size: 'medium',
  clickOnCell: () => undefined,
  handleSubmit: () => undefined,
  unique: '',
  loading: false,
  defaultChecked: [],
};
